import { supabase } from "@/integrations/supabase/client";

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

/**
 * Compress an image file using canvas resize (max 1200px width).
 */
export async function compressImage(file: File, maxWidth = 1200, quality = 0.8): Promise<Blob> {
  return new Promise((resolve, reject) => {
    if (!file.type.startsWith("image/")) {
      resolve(file);
      return;
    }
    const img = new Image();
    img.onload = () => {
      let { width, height } = img;
      if (width <= maxWidth) {
        resolve(file);
        return;
      }
      const ratio = maxWidth / width;
      width = maxWidth;
      height = Math.round(height * ratio);
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      if (!ctx) { resolve(file); return; }
      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : resolve(file)),
        "image/jpeg",
        quality
      );
    };
    img.onerror = () => reject(new Error("Failed to load image for compression"));
    img.src = URL.createObjectURL(file);
  });
}

/**
 * Validate file size. Returns error message if too large.
 */
export function validateFileSize(file: File): string | null {
  if (file.size > MAX_FILE_SIZE) {
    const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
    return `File is ${sizeMB}MB. Maximum allowed is 5MB.`;
  }
  return null;
}

/**
 * Upload a file and return a signed URL (1 hour expiry).
 * Compresses images automatically.
 */
export async function uploadAndGetSignedUrl(
  bucket: string,
  path: string,
  file: File | Blob
): Promise<{ signedUrl: string | null; error: string | null }> {
  try {
    // Compress if image
    let uploadFile: File | Blob = file;
    if (file instanceof File && file.type.startsWith("image/")) {
      uploadFile = await compressImage(file);
    }

    const { error: uploadErr } = await supabase.storage.from(bucket).upload(path, uploadFile);
    if (uploadErr) return { signedUrl: null, error: uploadErr.message };

    const { data, error } = await supabase.storage.from(bucket).createSignedUrl(path, 3600);
    if (error) return { signedUrl: null, error: error.message };

    return { signedUrl: data.signedUrl, error: null };
  } catch (e: any) {
    return { signedUrl: null, error: e.message || "Upload failed" };
  }
}

/**
 * Get a signed URL for an existing file path stored in the DB.
 */
export async function getSignedUrl(
  bucket: string,
  storedUrl: string
): Promise<string> {
  const pathMatch = storedUrl.match(/\/object\/(?:public|sign)\/[^/]+\/(.+?)(?:\?|$)/);
  const path = pathMatch ? decodeURIComponent(pathMatch[1]) : storedUrl;

  const { data, error } = await supabase.storage.from(bucket).createSignedUrl(path, 3600);
  if (error || !data) return storedUrl;
  return data.signedUrl;
}

/**
 * Hook-friendly: refresh signed URLs for an array of items with a url field.
 */
export async function refreshSignedUrls<T extends Record<string, any>>(
  items: T[],
  urlField: string,
  bucket: string = "couple-uploads"
): Promise<T[]> {
  return Promise.all(
    items.map(async (item) => {
      if (!item[urlField]) return item;
      const signedUrl = await getSignedUrl(bucket, item[urlField]);
      return { ...item, [urlField]: signedUrl };
    })
  );
}
